import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Camera, MapPin, RefreshCcw, FilePlus2, ScanEye, Wand2, Plus, Trash2, Search as SearchIcon, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtINR } from "../../lib/api";
import { compressToDataUrl } from "../../lib/imageCompress";
import { getStrictPosition, warmUpGps, rememberPhotoGps, photoGpsFor } from "../../lib/gps";
import { GpsRequiredDialog } from "../../components/GpsRequiredDialog";
import { StaffLabel } from "../../components/StaffAvatar";
import { Switch } from "../../components/ui/switch";
import { AssessmentComparison } from "../../components/AssessmentComparison";
import { FloorAssessment } from "../../components/FloorAssessment";
import { FloorEditorFull, NewDemandAmount, useAssessmentMasters, useSilentDemandCalc, emptyFloor } from "../../components/FloorEditorFull";
import { ApproverSelect } from "../../components/ApproverSelect";
import { DocumentUploads } from "../../components/DocumentUploads";
import { Checkbox } from "../../components/ui/checkbox";
import { getPack, queueAdd, useOnline, useQueue, syncAll, removeQueueItem, isNetworkError } from "../../lib/offline";
import { WifiOff, CloudUpload } from "lucide-react";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel } from "../../components/FeatureGate";
import { SafQueue } from "../../components/saf/SafQueue";   // Phase 207
import { ClipboardCheck } from "lucide-react";

const fmtDateTimeLocal = (s) => {
  try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

/** Multi-FY picker: demands can be generated for the current and/or previous financial years. */
export function FyMultiSelect({ fys = [], value = [], onChange, testIdPrefix = "fy" }) {
  if (!fys.length) return null;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <Card className="shadow-sm" data-testid={`${testIdPrefix}-multi-card`}>
      <CardContent className="p-3 space-y-1.5">
        <p className="text-sm font-medium">Demand financial year(s)</p>
        <p className="text-[11px] text-muted-foreground">Select current and/or previous years — tax is calculated with each year's own rates and a separate demand is generated per year.</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {fys.map((f) => (
            <label key={f.id} className={`flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 text-sm cursor-pointer ${value.includes(f.id) ? "border-primary bg-accent" : ""}`}>
              <Checkbox checked={value.includes(f.id)} onCheckedChange={() => toggle(f.id)}
                data-testid={`${testIdPrefix}-check-${f.fy_label}`} />
              {f.fy_label}{f.is_current ? " (current)" : ""}
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function useMasterOptions(code) {
  const [opts, setOpts] = useState({});
  useEffect(() => {
    const keys = ["zones", "wards", "mohallas", "property-types", "categories", "sub-categories", "usage-types", "sub-usage-types", "construction-types", "road-widths", "floors"];
    Promise.all(keys.map((k) => api.get(`/t/${code}/masters/${k}`, { params: { include_inactive: false } }).then((r) => [k, r.data]).catch(() => [k, null])))
      .then(async (pairs) => {
        // offline fallback: fill any failed fetch from the cached offline pack
        if (pairs.some(([, v]) => v === null)) {
          const pack = await getPack(code).catch(() => null);
          pairs = pairs.map(([k, v]) => [k, v ?? pack?.masters?.[k] ?? []]);
        }
        setOpts(Object.fromEntries(pairs));
      }).catch(() => {});
  }, [code]);
  return opts;
}

export function PhotoCapture({ code, photos, setPhotos }) {
  const [stage, setStage] = useState(null);          // null | "locating" | "uploading"
  const [gpsDialog, setGpsDialog] = useState(false);
  const inputRef = useRef(null);
  const openCamera = () => { warmUpGps(); inputRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    // Phase 132: GPS is mandatory for every field photo — no fix, no photo
    setStage("locating");
    let gps;
    try {
      gps = await getStrictPosition();
    } catch (err) {
      setStage(null);
      setGpsDialog(true);
      return;
    }
    setStage("uploading");
    const captured_at = new Date().toISOString();
    try {
      // Phase 130: shrink the capture to a <= 200 KB JPEG on the device (saves data + keeps the offline queue small)
      const b64 = await compressToDataUrl(file);
      const keepLocal = (msg) => {
        rememberPhotoGps(b64, { ...gps, captured_at });   // GPS travels with the photo until it syncs
        setPhotos([...photos, b64]);
        toast.success(msg);
      };
      if (!navigator.onLine) {
        keepLocal("Photo saved on device — uploads when back online");
        return;
      }
      try {
        const r = await api.post(`/t/${code}/files/photo`, { data: b64, filename: file.name.replace(/\.[^.]+$/, "") + ".jpg",
          lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy, captured_at, purpose: "field_photo" });
        setPhotos([...photos, r.data.url]);
        toast.success(`Photo added with GPS (${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)})`);
      } catch (err) {
        if (isNetworkError(err)) {
          keepLocal("No network — photo saved on device, uploads when back online");
        } else {
          toast.error(errMsg(err));
        }
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setStage(null);
    }
  };
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-2">
        {photos.map((p, i) => (
          <img key={i} src={p.startsWith("data:") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`} alt="site" className="h-16 w-16 object-cover rounded-lg border" />
        ))}
      </div>
      <button type="button" onClick={openCamera} disabled={!!stage}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed rounded-xl py-3 cursor-pointer text-sm text-muted-foreground hover:bg-muted/50 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="photo-capture-button">
        <Camera className="h-4 w-4" />
        {stage === "locating" ? "Getting GPS location..." : stage === "uploading" ? "Uploading..." : "Add GPS photo"}
      </button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="photo-input" />
      <GpsRequiredDialog open={gpsDialog} onOpenChange={setGpsDialog} onRetry={() => { setGpsDialog(false); setTimeout(openCamera, 50); }}
        testId="photo-capture-gps-required" />
    </div>
  );
}

export function useGPS(code) {
  const [gps, setGps] = useState(null);
  const capture = () => {
    if (!navigator.geolocation) return toast.error("GPS not available on this device");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setGps(loc);
        toast.success("GPS location captured");
        if (code) {
          api.get(`/t/${code}/digipin`, { params: { lat: loc.latitude, lng: loc.longitude } })
            .then((r) => setGps((g) => ({ ...g, digipin: r.data.digipin })))
            .catch(() => {});
        }
      },
      () => toast.error("Could not get GPS location"),
      { timeout: 8000 }
    );
  };
  return [gps, capture];
}

export function GpsButton({ gps, onCapture, testId = "capture-gps-button" }) {
  return (
    <div className="space-y-1">
      <Button variant="outline" className="w-full" onClick={onCapture} data-testid={testId}>
        <MapPin className="h-4 w-4 mr-2" />
        {gps ? `GPS: ${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}` : "Capture GPS location"}
      </Button>
      {gps?.digipin && (
        <p className="text-xs text-center font-mono font-medium text-primary" data-testid="digipin-display">
          DIGIPIN: {gps.digipin} <span className="text-muted-foreground font-sans">(India Post)</span>
        </p>
      )}
    </div>
  );
}

const MasterSelect = ({ label, options, valueKey, labelKey, value, onChange, testId }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid={testId}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
      <SelectContent>
        {(options || []).map((o) => <SelectItem key={o.id} value={o.id}>{o[labelKey]}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export function ReassessmentForm() {
  const { code, propId } = useParams();
  const navigate = useNavigate();
  const online = useOnline();
  const opts = useMasterOptions(code);
  const masters = useAssessmentMasters(code);
  const [prop, setProp] = useState(null);
  const [demands, setDemands] = useState([]);
  const [raId, setRaId] = useState("");
  const [mode, setMode] = useState(""); // "" | retain | revise
  const [form, setForm] = useState({ reason: "" });
  const [ownerChanged, setOwnerChanged] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [gps, captureGps] = useGPS(code);
  const [busy, setBusy] = useState(false);
  const [floors, setFloors] = useState([]);
  const [aiResult, setAiResult] = useState(null);
  const [approverId, setApproverId] = useState("");
  const [docs, setDocs] = useState([]);
  const [docDueDate, setDocDueDate] = useState("");
  const [fyIds, setFyIds] = useState([]);
  useEffect(() => { if (masters.fyId) setFyIds((v) => (v.length ? v : [masters.fyId])); }, [masters.fyId]);
  const mapSaved = (saved) => (saved || []).map((f) => ({
    floor_label: f.floor_label || "", house_no: f.house_no || "",
    usage_type_id: f.usage_type_id || "", sub_usage_id: f.sub_usage_id || "",
    property_type_id: f.property_type_id || "", const_type_id: f.const_type_id || "",
    fee_category_id: f.fee_category_id || "", fee_sub_category_id: f.fee_sub_category_id || "",
    area_sqft: f.constructed_area_sqft ?? f.area_sqft ?? "",
    plot_area_sqft: f.plot_area_sqft ?? "", open_area_sqft: f.open_area_sqft ?? "",
    carpet_area_sqft: f.carpet_area_sqft ?? "",
    electricity_ca_no: f.electricity_ca_no || "", construction_date: f.construction_date || "",
    construction_age: f.construction_age ?? "",
  }));

  useEffect(() => {
    const applyDetail = (p, dms) => {
      setProp(p);
      setDemands(dms || []);
      setFloors(mapSaved(p?.assessment_floors));
      setForm((f) => ({
        ...f,
        new_mobile: p?.mobile || "", new_address: p?.address || "",
        new_road_width_id: p?.road_width_id || "",
      }));
    };
    api.get(`/t/${code}/properties/${propId}`)
      .then((r) => applyDetail(r.data.property, r.data.demands))
      .catch(async () => {
        // offline fallback: assigned-property detail from the cached pack
        const pack = await getPack(code).catch(() => null);
        const p = (pack?.assigned_properties || []).find((x) => x.id === propId);
        if (p) applyDetail(p, pack?.demands_by_property?.[propId] || []);
      });
    // reserve the unique reassessment ID as soon as the form opens (skipped offline)
    api.post(`/t/${code}/reassessments/start`, { property_id: propId })
      .then((r) => setRaId(r.data.reassessment_id)).catch(() => {});
  }, [code, propId]);

  const usageName = (id) => (opts["usage-types"] || []).find((u) => u.id === id)?.usage_type_name || "";
  const subUsageName = (id) => (opts["sub-usage-types"] || []).find((s) => s.id === id)?.sub_usage_name || "";
  const ptypeName = (id) => (opts["property-types"] || []).find((p) => p.id === id)?.type_name || "";
  const ctypeName = (id) => (opts["construction-types"] || []).find((c) => c.id === id)?.const_type_name || "";
  const nameOf = (key, id, labelKey) => (opts[key] || []).find((x) => x.id === id)?.[labelKey] || "—";

  // silent computation against the admin Rate Engine (no rates shown to collector)
  const calc = useSilentDemandCalc(code, fyIds.length ? fyIds : masters.fyId, mode === "revise" ? floors : [], prop?.owner_name,
    { road_width_id: form.new_road_width_id || prop?.road_width_id });

  // ---- no-change detection: collector values vs saved data-entry values ----
  const norm = (fls, constructedKey) => (fls || []).map((f) => [
    f.property_type_id || "", f.const_type_id || "", f.usage_type_id || "", f.sub_usage_id || "",
    (f.house_no || "").trim(), (f.electricity_ca_no || "").trim(), f.construction_date || "", f.construction_age ?? "",
    Number(f.plot_area_sqft) || 0, Number(f.open_area_sqft) || 0,
    Number(f[constructedKey] ?? f.area_sqft) || 0, Number(f.carpet_area_sqft) || 0,
  ].join("|")).join("~");
  const savedFloors = prop?.assessment_floors || [];
  const unchanged = savedFloors.length > 0 && floors.length > 0 &&
    norm(savedFloors, "constructed_area_sqft") === norm(floors, "area_sqft");

  const applyAi = (result) => {
    setAiResult(result);
    const suggestions = result.floor_usage_suggestion || [];
    if (suggestions.length) {
      setFloors(suggestions.map((fl) => {
        const usage = (opts["usage-types"] || []).find((u) => (u.usage_type_name || "").toLowerCase().includes((fl.usage_type || "").toLowerCase()));
        const sbu = Number(fl.super_builtup_area_sqft) || "";
        return {
          floor_label: fl.floor_label || "",
          usage_type_id: usage?.id || "", sub_usage_id: "", property_type_id: "",
          fee_category_id: "", fee_sub_category_id: "",
          area_sqft: sbu, plot_area_sqft: "", open_area_sqft: "",
          carpet_area_sqft: sbu ? Math.round(sbu * 0.8) : "",
        };
      }));
      toast.success("AI estimates applied to the floor editor — verify and adjust actual values");
    }
  };

  const buildFloorPayload = (fls) => fls.map((f) => ({
    floor_label: f.floor_label || "Floor", house_no: (f.house_no || "").trim() || null,
    property_type_id: f.property_type_id || null, property_type_name: ptypeName(f.property_type_id) || null,
    const_type_id: f.const_type_id || null, const_type_name: ctypeName(f.const_type_id) || null,
    usage_type_id: f.usage_type_id || null, usage_type_name: usageName(f.usage_type_id) || null,
    sub_usage_id: f.sub_usage_id || null, sub_usage_name: subUsageName(f.sub_usage_id) || null,
    fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
    plot_area_sqft: Number(f.plot_area_sqft) || 0, open_area_sqft: Number(f.open_area_sqft) || 0,
    constructed_area_sqft: Number(f.area_sqft) || 0, carpet_area_sqft: Number(f.carpet_area_sqft) || 0,
    electricity_ca_no: (f.electricity_ca_no || "").trim() || null,
    construction_date: f.construction_date || null,
    construction_age: f.construction_age === "" || f.construction_age == null ? null : Number(f.construction_age),
  }));

  const submit = async (retain = false) => {
    if (!form.reason) return toast.error("Reason is required");
    if (ownerChanged && !(form.new_owner_name || "").trim()) return toast.error("Enter the new owner name for the transfer case");
    if (online && !retain && floors.length > 0 && !calc?.result) {
      return toast.error("Demand calculation in progress — enter usage type and constructed area for each floor");
    }
    if (!online && !retain && floors.length > 0 && !floors.some((f) => Number(f.area_sqft) > 0 && f.usage_type_id)) {
      return toast.error("Enter usage type and constructed area for at least one floor — the amount is calculated when this syncs");
    }
    setBusy(true);
    try {
      const payload = {
        property_id: propId, reassessment_id: raId, retained_as_is: retain,
        owner_changed: ownerChanged, ...form, photos, ...(gps || {}),
        documents: docs, doc_collect_date: docDueDate || null,
        financial_year_ids: fyIds,
      };
      if (retain || (form.new_road_width_id || "") === (prop?.road_width_id || "")) delete payload.new_road_width_id;
      if (!ownerChanged) { delete payload.new_owner_name; delete payload.transfer_remarks; }
      if (aiResult) payload.ai_analysis = aiResult;
      if (approverId) payload.approver_id = approverId;
      if (!retain && calc?.result) {
        payload.assessment = calc.result;
        payload.proposed_demands = calc.result.proposed_demands;
        payload.floors_detail = calc.inputs.floors.map((f) => ({
          floor_label: f.floor_label, owner_name: f.owner_name || null,
          usage_type_id: f.usage_type_id, usage_type: usageName(f.usage_type_id).toLowerCase(),
          super_builtup_area_sqft: f.area_sqft,
          fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
        }));
        payload.super_builtup_area_sqft = calc.result.total_constructed_area_sqft;
        payload.carpet_area_sqft = calc.result.carpet_area_sqft;
      }
      payload.new_floors = buildFloorPayload(retain ? mapSaved(savedFloors) : floors);
      // construction type is captured per floor now — keep the property-level record in sync from the first floor that has one
      if (!retain) {
        const fc = floors.find((f) => f.const_type_id);
        if (fc) payload.new_const_type_id = fc.const_type_id;
        const hnos = [...new Set(floors.map((f) => (f.house_no || "").trim()).filter(Boolean))];
        if (hnos.length > 0) payload.new_house_no = hnos.join(", ");
      }

      // offline path: save the whole reassessment on the device and sync later
      const saveOffline = async () => {
        const local = (payload.photos || []).filter((p) => p.startsWith("data:"))
          .map((data, i) => ({ data, filename: `reassess-offline-${i + 1}.jpg`, ...(photoGpsFor(data) || {}) }));   // Phase 132: GPS rides along
        payload.photos = (payload.photos || []).filter((p) => !p.startsWith("data:"));
        delete payload.reassessment_id; // the server assigns the real ID during sync
        const validFloors = (retain ? [] : floors).filter((f) => Number(f.area_sqft) > 0 && f.usage_type_id);
        const fyList = fyIds.length ? fyIds : (masters.fyId ? [masters.fyId] : []);
        await queueAdd(code, "reassessment", payload, {
          photos: local,
          calcInputs: validFloors.length ? {
            fyIds: fyList, floors: validFloors,
            road_width_id: form.new_road_width_id || prop?.road_width_id || null,
            owner_name: prop?.owner_name || null,
          } : null,
          label: `Reassessment • ${prop?.pin || ""} ${prop?.owner_name || ""}`.trim(),
        });
        toast.success("Reassessment saved on device — it will submit automatically when you're back online");
        navigate(`/${code}/collector/field`);
      };

      if (!online) { await saveOffline(); return; }
      try {
        // photos captured while briefly offline: upload them before submitting
        const localPhotos = (payload.photos || []).filter((p) => p.startsWith("data:"));
        if (localPhotos.length) {
          const urls = [];
          for (let i = 0; i < localPhotos.length; i++) {
            const gps = photoGpsFor(localPhotos[i]);   // Phase 132: captured with GPS on the device
            const r = await api.post(`/t/${code}/files/photo`, { data: localPhotos[i], filename: `reassess-photo-${i + 1}.jpg`,
              ...(gps ? { ...gps, purpose: "field_photo" } : {}) });
            urls.push(r.data.url);
          }
          payload.photos = [...(payload.photos || []).filter((p) => !p.startsWith("data:")), ...urls];
        }
        const r = await api.post(`/t/${code}/reassessments`, payload);
        if (r.data.status === "no_change") {
          toast.success(`Reassessment ${r.data.reassessment_id} recorded — existing details retained. Proceed to collect payment.`);
          navigate(`/${code}/collector/property/${propId}`);
        } else {
          toast.success(`Reassessment ${r.data.reassessment_id} submitted for review${!retain && calc?.result ? ` • ${calc.result.demand_count} demand group(s) proposed` : ""}${r.data.is_transfer_case ? ` • Transfer case ${r.data.transfer_id} recorded` : ""}`);
          navigate(`/${code}/collector/field`);
        }
      } catch (err) {
        if (isNetworkError(err)) { await saveOffline(); return; }
        throw err;
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const totalBalance = demands.reduce((s, d) => s + (d.balance_amount || 0), 0);
  const totArea = (key) => savedFloors.reduce((s, f) => s + (Number(f[key]) || 0), 0);

  const transferBlock = (
    <Card className={ownerChanged ? "border-amber-300 bg-amber-50/50" : ""} data-testid="transfer-case-card">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Change in property owner?</p>
            <p className="text-[11px] text-muted-foreground">Owner-name changes are recorded and tracked as a Transfer Case.</p>
          </div>
          <Switch checked={ownerChanged} onCheckedChange={setOwnerChanged} data-testid="owner-changed-switch" />
        </div>
        {ownerChanged && (
          <div className="space-y-2">
            <div className="space-y-1">
              <Label>New owner name *</Label>
              <Input className="h-11" value={form.new_owner_name || ""} placeholder={`Current: ${prop?.owner_name || ""}`}
                onChange={(e) => setForm({ ...form, new_owner_name: e.target.value })} data-testid="reassessment-owner-input" />
            </div>
            <div className="space-y-1">
              <Label>Transfer remarks</Label>
              <Input className="h-11" value={form.transfer_remarks || ""} placeholder="e.g. Sale deed / inheritance"
                onChange={(e) => setForm({ ...form, transfer_remarks: e.target.value })} data-testid="transfer-remarks-input" />
            </div>
            <p className="text-[11px] text-amber-800">A Transfer Case ID will be generated and sent with this reassessment for approval.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold font-heading flex items-center gap-2"><RefreshCcw className="h-5 w-5" /> Reassessment</h1>
      {!online && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-amber-900" data-testid="reassess-offline-banner">
          <WifiOff className="h-4 w-4 shrink-0" />
          <p className="text-xs">You're offline — this reassessment is saved on your device and submits automatically when the network returns. The ID and tax amount are assigned during sync.</p>
        </div>
      )}
      {raId && (
        <div className="rounded-lg border border-primary/30 bg-accent/50 px-3 py-2 flex items-center justify-between" data-testid="reassessment-id-banner">
          <span className="text-xs text-muted-foreground">Reassessment ID</span>
          <span className="font-mono font-semibold text-sm text-primary" data-testid="reassessment-id-value">{raId}</span>
        </div>
      )}
      {prop && (
        <Card className="shadow-sm" data-testid="reassess-existing-data">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm font-heading">Existing record (as uploaded by municipality)</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1 space-y-2 text-sm">
            <div className="flex items-center justify-between flex-wrap gap-1">
              <span><span className="font-mono font-semibold">{prop.pin}</span> • {prop.owner_name}</span>
              {prop.digipin && <span className="text-xs font-mono text-primary">DIGIPIN {prop.digipin}</span>}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs" data-testid="reassess-property-fields">
              <span className="text-muted-foreground">Mobile</span><span>{prop.mobile || "—"}</span>
              <span className="text-muted-foreground">Address</span><span>{prop.address || "—"}</span>
              <span className="text-muted-foreground">Zone</span><span>{nameOf("zones", prop.zone_id, "zone_name")}</span>
              <span className="text-muted-foreground">Ward</span><span>{nameOf("wards", prop.ward_id, "ward_name")}</span>
              <span className="text-muted-foreground">Mohalla</span><span>{nameOf("mohallas", prop.mohalla_id, "mohalla_name")}</span>
              <span className="text-muted-foreground">Category</span><span>{nameOf("categories", prop.category_id, "category_name")}</span>
              <span className="text-muted-foreground">Construction</span><span>{nameOf("construction-types", prop.const_type_id, "const_type_name")}</span>
              <span className="text-muted-foreground">Road width</span><span data-testid="reassess-road-width">{nameOf("road-widths", prop.road_width_id, "width_label")}</span>
              <span className="text-muted-foreground">Areas (P/O/C/C)</span>
              <span>{totArea("plot_area_sqft")} / {totArea("open_area_sqft")} / {totArea("constructed_area_sqft")} / {totArea("carpet_area_sqft")} sqft</span>
            </div>
            <FloorAssessment floors={savedFloors} />
            {demands.length > 0 ? (
              <div className="border rounded-lg divide-y" data-testid="reassess-existing-demands">
                {demands.map((d) => (
                  <div key={d.id} className="px-2.5 py-1.5 flex items-center justify-between text-xs">
                    <span>{d.tax_head_name} <span className="text-muted-foreground">• {d.fy_label}</span></span>
                    <span className="tabular-nums">
                      {fmtINR(d.total_demand)} <span className={d.balance_amount > 0 ? "text-amber-700" : "text-emerald-700"}>({d.balance_amount > 0 ? `${fmtINR(d.balance_amount)} due` : "paid"})</span>
                    </span>
                  </div>
                ))}
                <div className="px-2.5 py-1.5 flex items-center justify-between text-xs font-semibold">
                  <span>Total outstanding</span><span className="tabular-nums">{fmtINR(totalBalance)}</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No demands on record for this property.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* mode choice: retain everything as-is OR revise the details */}
      <div className="grid grid-cols-2 gap-2" data-testid="reassess-mode-choice">
        <button type="button" onClick={() => setMode("retain")}
          className={`rounded-xl border p-3 text-left transition-colors ${mode === "retain" ? "border-primary bg-accent" : "hover:bg-muted/50"}`}
          data-testid="mode-retain-button">
          <p className="text-sm font-semibold">Retain as-is</p>
          <p className="text-[11px] text-muted-foreground">All details verified correct — keep the existing record and demand.</p>
        </button>
        <button type="button" onClick={() => { setMode("revise"); if (floors.length === 0) setFloors([emptyFloor(0)]); }}
          className={`rounded-xl border p-3 text-left transition-colors ${mode === "revise" ? "border-primary bg-accent" : "hover:bg-muted/50"}`}
          data-testid="mode-revise-button">
          <p className="text-sm font-semibold">Enter revised details</p>
          <p className="text-[11px] text-muted-foreground">Update fields and floor-wise areas found different in the field.</p>
        </button>
      </div>

      {mode && (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Reason / field observation *</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder={mode === "retain" ? "e.g. Field verification done — details match the record" : "e.g. Additional floor constructed"}
              data-testid="reassessment-reason-input" />
          </div>

          {transferBlock}

          {mode === "revise" && (
            <>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1"><Label>Mobile</Label><Input className="h-11" value={form.new_mobile || ""} onChange={(e) => setForm({ ...form, new_mobile: e.target.value })} data-testid="reassessment-mobile-input" /></div>
                <div className="space-y-1"><Label>Address</Label><Input className="h-11" value={form.new_address || ""} onChange={(e) => setForm({ ...form, new_address: e.target.value })} /></div>
                <MasterSelect label="Road width" options={opts["road-widths"] || []} labelKey="width_label"
                  value={form.new_road_width_id} onChange={(v) => setForm({ ...form, new_road_width_id: v })}
                  testId="reassessment-roadwidth-select" />
              </div>

              <FyMultiSelect fys={masters.fys} value={fyIds} onChange={setFyIds} testIdPrefix="reassess-fy" />

              {online && (
                <AiScanCard code={code} photos={photos} setPhotos={setPhotos} onResult={setAiResult} onApply={applyAi}
                  applyLabel="Apply estimates to floor editor" />
              )}

              <Card className="shadow-sm">
                <CardContent className="p-3">
                  <FloorEditorFull masters={masters} floors={floors} setFloors={setFloors} testIdPrefix="reassess" />
                </CardContent>
              </Card>

              {online ? <NewDemandAmount calc={calc} /> : (
                <Card className="shadow-sm border-amber-300/60" data-testid="reassess-offline-calc-note">
                  <CardContent className="p-3">
                    <p className="text-xs text-amber-900 flex items-center gap-2">
                      <CloudUpload className="h-4 w-4 shrink-0" />
                      The demand amount is calculated automatically from the municipality's rate configuration when this reassessment syncs.
                    </p>
                  </CardContent>
                </Card>
              )}

              <AssessmentComparison ai={aiResult} original={savedFloors} current={floors}
                nameResolvers={{ usage: usageName, ptype: ptypeName }} />
            </>
          )}

          <PhotoCapture code={code} photos={photos} setPhotos={setPhotos} />
          {online ? (
            <DocumentUploads code={code} docTypes={masters.docTypes} docs={docs} setDocs={setDocs}
              dueDate={docDueDate} setDueDate={setDocDueDate} testIdPrefix="reassess-docs" />
          ) : (
            <p className="text-[11px] text-muted-foreground border border-dashed rounded-lg px-3 py-2" data-testid="reassess-offline-docs-note">
              Document upload needs internet — after this reassessment syncs, attach documents from Field operations → My submissions.
            </p>
          )}
          <GpsButton gps={gps} onCapture={captureGps} />

          {mode === "retain" || (mode === "revise" && unchanged && !ownerChanged) ? (
            <Card className="border-emerald-300 bg-emerald-50/60" data-testid="no-change-card">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-semibold text-emerald-800">
                  {mode === "retain" ? "Details retained as-is" : "No change detected during field verification"}
                </p>
                <p className="text-xs text-emerald-700">
                  The existing record and demand are kept unchanged — no approval is needed.
                  The reassessment {raId} will be recorded for tracking, then you can collect the payment.
                </p>
                <Button size="lg" className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" disabled={busy}
                  onClick={() => submit(true)} data-testid="retain-record-button">
                  {busy ? "Recording..." : !online ? "Record on device (offline)" : `Record & collect payment (${fmtINR(totalBalance)} due)`}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <ApproverSelect code={code} value={approverId} onChange={setApproverId} testId="reassessment-approver-select" />
              <Button size="lg" className="w-full h-12" onClick={() => submit(mode === "retain")} disabled={busy} data-testid="reassessment-submit-button">
                {busy ? "Submitting..." : !online ? "Save reassessment on device (offline)"
                  : calc?.result ? `Send for approval (${calc.result.demand_count} demand groups • ${fmtINR(calc.result.grand_total?.total_yearly || 0)}/yr)` : "Submit reassessment for approval"}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const USAGE_OPTIONS = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "mixed", label: "Mixed" },
];

export function AiScanCard({ code, photos, setPhotos, onResult, onApply, applyLabel = "Apply estimates to survey form" }) {
  const [scanPhoto, setScanPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [gpsDialog, setGpsDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const openCamera = () => { warmUpGps(); inputRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    // Phase 132: the scan photo is a field photo too — GPS mandatory
    setLocating(true);
    let gps;
    try {
      gps = await getStrictPosition();
    } catch (err) {
      setLocating(false);
      setGpsDialog(true);
      return;
    }
    setLocating(false);
    setUploading(true);
    setResult(null);
    try {
      const b64 = await compressToDataUrl(file);   // Phase 130: <= 200 KB JPEG before upload
      const r = await api.post(`/t/${code}/files/photo`, { data: b64, filename: file.name.replace(/\.[^.]+$/, "") + ".jpg",
        lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy, captured_at: new Date().toISOString(), purpose: "field_photo" });
      setScanPhoto(r.data.url);
      setPhotos([...photos, r.data.url]);
      toast.success(`Property photo captured with GPS (${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)})`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/t/${code}/ai/vision-survey`, { photo_url: scanPhoto });
      setResult(r.data);
      onResult && onResult(r.data);
      toast.success("AI analysis complete — review the estimates below");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Card className="shadow-sm border-primary/30" data-testid="ai-scan-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <ScanEye className="h-4 w-4" /> AI Property Scan
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Point your camera at the property. AI estimates floors, plot area and super built-up area,
          and suggests floor-wise usage. Carpet area is auto-derived as 80% of super built-up area.
        </p>
        {!scanPhoto ? (
          <button type="button" onClick={openCamera} disabled={uploading || locating}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-primary/40 rounded-xl py-4 cursor-pointer text-sm font-medium text-primary hover:bg-accent disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="ai-scan-capture-button">
            <Camera className="h-5 w-5" /> {locating ? "Getting GPS location..." : uploading ? "Uploading photo..." : "Capture property photo (GPS)"}
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${scanPhoto}`} alt="property" className="h-20 w-20 object-cover rounded-lg border" />
            <div className="flex-1 space-y-2">
              <Button className="w-full" onClick={analyze} disabled={analyzing} data-testid="ai-scan-analyze-button">
                <ScanEye className="h-4 w-4 mr-2" /> {analyzing ? "Analyzing structure..." : result ? "Re-analyze" : "Analyze with AI"}
              </Button>
              <button type="button" onClick={openCamera} disabled={uploading || locating}
                className="block w-full text-center text-xs text-muted-foreground underline cursor-pointer disabled:opacity-60"
                data-testid="ai-scan-retake-button">
                {locating ? "Getting GPS location..." : "Retake photo"}
              </button>
            </div>
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="ai-scan-photo-input" />
        <GpsRequiredDialog open={gpsDialog} onOpenChange={setGpsDialog} onRetry={() => { setGpsDialog(false); setTimeout(openCamera, 50); }}
          testId="ai-scan-gps-required" />
        {analyzing && (
          <p className="text-xs text-muted-foreground animate-pulse" data-testid="ai-scan-loading">
            AI is counting floors and estimating areas from the photo...
          </p>
        )}
        {result && (
          <div className="space-y-3" data-testid="ai-scan-result">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-muted rounded-lg p-2.5">
                <p className="text-[11px] text-muted-foreground">Floors detected</p>
                <p className="font-semibold">{result.floors_count}</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5">
                <p className="text-[11px] text-muted-foreground">Plot area (approx)</p>
                <p className="font-semibold">{Number(result.approx_plot_area_sqft || 0).toLocaleString("en-IN")} sqft</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5">
                <p className="text-[11px] text-muted-foreground">Super built-up (approx)</p>
                <p className="font-semibold">{Number(result.approx_super_builtup_area_sqft || 0).toLocaleString("en-IN")} sqft</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5">
                <p className="text-[11px] text-muted-foreground">Carpet area (80% of SBU)</p>
                <p className="font-semibold">{Number(result.carpet_area_sqft || 0).toLocaleString("en-IN")} sqft</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className={`px-2 py-0.5 rounded-full capitalize border ${
                result.confidence === "high" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : result.confidence === "medium" ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid="ai-scan-confidence">
                {result.confidence} confidence
              </span>
              {result.construction_type_guess && <span className="px-2 py-0.5 rounded-full bg-muted border">{result.construction_type_guess}</span>}
              {result.property_condition && <span className="px-2 py-0.5 rounded-full bg-muted border">{result.property_condition}</span>}
            </div>
            {(result.floor_usage_suggestion || []).length > 0 && (
              <div className="border rounded-lg divide-y">
                {result.floor_usage_suggestion.map((fl, i) => (
                  <div key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium">{fl.floor_label}</p>
                      <p className="text-[11px] text-muted-foreground">{fl.evidence}</p>
                    </div>
                    <div className="text-right">
                      <p className="capitalize text-xs font-medium">{fl.usage_type}</p>
                      <p className="text-[11px] text-muted-foreground">~{Number(fl.super_builtup_area_sqft || 0).toLocaleString("en-IN")} sqft</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => onApply(result)} data-testid="ai-scan-apply-button">
              <Wand2 className="h-4 w-4 mr-2" /> {applyLabel}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function FloorEditor({ floors, setFloors }) {
  const update = (i, patch) => setFloors(floors.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const remove = (i) => setFloors(floors.filter((_, idx) => idx !== i));
  const add = () => {
    const labels = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Fourth Floor", "Fifth Floor"];
    setFloors([...floors, { floor_label: labels[floors.length] || `Floor ${floors.length + 1}`, usage_type: "residential", super_builtup_area_sqft: "" }]);
  };
  const totalSbu = floors.reduce((s, f) => s + (Number(f.super_builtup_area_sqft) || 0), 0);
  return (
    <div className="space-y-2" data-testid="floor-editor">
      <div className="flex items-center justify-between">
        <Label>Floor-wise usage details</Label>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-floor-button">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add floor
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        If usage differs across floors (e.g. ground floor commercial, upper floors residential),
        separate demands per usage are generated after admin approval.
      </p>
      {floors.length === 0 && (
        <p className="text-xs text-muted-foreground border border-dashed rounded-lg py-3 text-center">
          No floors added. Use AI scan or add floors manually.
        </p>
      )}
      {floors.map((f, i) => (
        <div key={i} className="border rounded-lg p-2.5 grid grid-cols-[1fr_auto] gap-2 items-start" data-testid={`floor-row-${i}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input className="h-10" placeholder="Floor label" value={f.floor_label}
              onChange={(e) => update(i, { floor_label: e.target.value })} data-testid={`floor-label-input-${i}`} />
            <Select value={f.usage_type} onValueChange={(v) => update(i, { usage_type: v })}>
              <SelectTrigger className="h-10" data-testid={`floor-usage-select-${i}`}><SelectValue placeholder="Usage" /></SelectTrigger>
              <SelectContent>
                {USAGE_OPTIONS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className="h-10" type="number" placeholder="Super built-up sqft" value={f.super_builtup_area_sqft}
              onChange={(e) => update(i, { super_builtup_area_sqft: e.target.value ? Number(e.target.value) : "" })}
              data-testid={`floor-area-input-${i}`} />
          </div>
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground" onClick={() => remove(i)}
            data-testid={`remove-floor-button-${i}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {floors.length > 0 && (
        <div className="flex justify-between text-xs text-muted-foreground px-1">
          <span>Total super built-up: <span className="font-semibold text-foreground">{totalSbu.toLocaleString("en-IN")} sqft</span></span>
          <span>Carpet (80%): <span className="font-semibold text-foreground">{Math.round(totalSbu * 0.8).toLocaleString("en-IN")} sqft</span></span>
        </div>
      )}
    </div>
  );
}

export function SurveyForm() {
  const { code } = useParams();
  const navigate = useNavigate();
  const online = useOnline();
  const opts = useMasterOptions(code);
  const masters = useAssessmentMasters(code);
  const [svId, setSvId] = useState("");
  const [form, setForm] = useState({});
  const [zoneOpts, setZoneOpts] = useState([]);
  const [wardOpts, setWardOpts] = useState([]);
  const [mohallaOpts, setMohallaOpts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [floors, setFloors] = useState([emptyFloor(0)]);
  const [aiResult, setAiResult] = useState(null);
  const [gps, captureGps] = useGPS(code);
  const [busy, setBusy] = useState(false);
  const [approverId, setApproverId] = useState("");
  const [docs, setDocs] = useState([]);
  const [docDueDate, setDocDueDate] = useState("");
  const [fyIds, setFyIds] = useState([]);
  useEffect(() => { if (masters.fyId) setFyIds((v) => (v.length ? v : [masters.fyId])); }, [masters.fyId]);

  useEffect(() => {
    // reserve the unique New Survey ID as soon as the form opens
    api.post(`/t/${code}/surveys/start`).then((r) => setSvId(r.data.survey_id)).catch(() => {});
  }, [code]);
  // strict=true: territory dropdowns limited to the collector's MAPPED territory only
  // (explicit property assignments do not widen these — Phase 45). Offline: cached pack.
  useEffect(() => {
    api.get(`/t/${code}/masters/zones`, { params: { include_inactive: false, strict: true } })
      .then((r) => setZoneOpts(r.data))
      .catch(async () => setZoneOpts((await getPack(code).catch(() => null))?.masters?.zones || []));
  }, [code]);
  useEffect(() => {
    if (!form.zone_id) return;
    api.get(`/t/${code}/masters/wards`, { params: { parent_id: form.zone_id, include_inactive: false, strict: true } })
      .then((r) => setWardOpts(r.data))
      .catch(async () => setWardOpts(((await getPack(code).catch(() => null))?.masters?.wards || []).filter((w) => w.zone_id === form.zone_id)));
  }, [form.zone_id, code]);
  useEffect(() => {
    if (!form.ward_id) return;
    api.get(`/t/${code}/masters/mohallas`, { params: { parent_id: form.ward_id, include_inactive: false, strict: true } })
      .then((r) => setMohallaOpts(r.data))
      .catch(async () => setMohallaOpts(((await getPack(code).catch(() => null))?.masters?.mohallas || []).filter((m) => m.ward_id === form.ward_id)));
  }, [form.ward_id, code]);

  const usageName = (id) => (opts["usage-types"] || []).find((u) => u.id === id)?.usage_type_name || "";
  const subUsageName = (id) => (opts["sub-usage-types"] || []).find((s) => s.id === id)?.sub_usage_name || "";
  const ptypeName = (id) => (opts["property-types"] || []).find((p) => p.id === id)?.type_name || "";
  const ctypeName = (id) => (opts["construction-types"] || []).find((c) => c.id === id)?.const_type_name || "";
  const matchUsage = (guess) => {
    const g = (guess || "").toLowerCase();
    const hit = (opts["usage-types"] || []).find((u) => u.usage_type_name.toLowerCase().includes(g) || g.includes(u.usage_type_name.toLowerCase()));
    return hit?.id || "";
  };

  // silent computation against the admin Rate Engine (no rates shown)
  const calc = useSilentDemandCalc(code, fyIds.length ? fyIds : masters.fyId, floors, form.owner_name,
    { road_width_id: form.road_width_id });

  const applyScan = (scan) => {
    setForm((f) => ({ ...f, total_area_plot_sqft: scan.approx_plot_area_sqft ?? f.total_area_plot_sqft }));
    const rows = (scan.floor_usage_suggestion || []).map((fl, i) => {
      const sbu = Number(fl.super_builtup_area_sqft) || "";
      return { ...emptyFloor(i), floor_label: fl.floor_label || "", usage_type_id: matchUsage(fl.usage_type),
               area_sqft: sbu, carpet_area_sqft: sbu ? Math.round(sbu * 0.8) : "" };
    });
    if (rows.length) setFloors(rows);
    toast.success("AI estimates applied to the floor editor — verify and adjust actual values");
  };

  const tot = (key) => floors.reduce((s, f) => s + (Number(f[key]) || 0), 0);
  const sbu = tot("area_sqft");
  const carpet = tot("carpet_area_sqft");

  const submit = async () => {
    if (!form.owner_name) return toast.error("Owner name is required");
    if (!form.zone_id || !form.ward_id) return toast.error("Zone and ward are required");
    setBusy(true);
    try {
      const payload = { ...form, survey_id: svId, photos, ...(gps || {}),
        documents: docs, doc_collect_date: docDueDate || null, financial_year_ids: fyIds };
      const validFloors = floors.filter((f) => Number(f.area_sqft) > 0 && f.usage_type_id);
      if (calc?.result) {
        payload.assessment = calc.result;
        payload.proposed_demands = calc.result.proposed_demands;
        payload.floors_detail = calc.inputs.floors.map((f) => ({
          floor_label: f.floor_label, owner_name: f.owner_name || null,
          usage_type_id: f.usage_type_id, usage_type: usageName(f.usage_type_id).toLowerCase(),
          super_builtup_area_sqft: f.area_sqft,
          fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
        }));
      } else if (validFloors.length) {
        // offline: keep the floor detail — the demand assessment is computed at sync time
        payload.floors_detail = validFloors.map((f) => ({
          floor_label: f.floor_label, owner_name: null,
          usage_type_id: f.usage_type_id, usage_type: usageName(f.usage_type_id).toLowerCase(),
          super_builtup_area_sqft: f.area_sqft,
          fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
        }));
      }
      payload.new_floors = floors.map((f) => ({
        floor_label: f.floor_label || "Floor", house_no: (f.house_no || "").trim() || null,
        property_type_id: f.property_type_id || null, property_type_name: ptypeName(f.property_type_id) || null,
        const_type_id: f.const_type_id || null, const_type_name: ctypeName(f.const_type_id) || null,
        usage_type_id: f.usage_type_id || null, usage_type_name: usageName(f.usage_type_id) || null,
        sub_usage_id: f.sub_usage_id || null, sub_usage_name: subUsageName(f.sub_usage_id) || null,
        fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
        plot_area_sqft: Number(f.plot_area_sqft) || 0, open_area_sqft: Number(f.open_area_sqft) || 0,
        constructed_area_sqft: Number(f.area_sqft) || 0, carpet_area_sqft: Number(f.carpet_area_sqft) || 0,
        electricity_ca_no: (f.electricity_ca_no || "").trim() || null,
        construction_date: f.construction_date || null,
        construction_age: f.construction_age === "" || f.construction_age == null ? null : Number(f.construction_age),
      }));
      // property/construction type are captured per floor now — derive the property-level values from the first floor that has one
      const fp = floors.find((f) => f.property_type_id);
      if (fp) payload.property_type_id = fp.property_type_id;
      const fc = floors.find((f) => f.const_type_id);
      if (fc) payload.const_type_id = fc.const_type_id;
      const hnos = [...new Set(floors.map((f) => (f.house_no || "").trim()).filter(Boolean))];
      if (hnos.length > 0) payload.house_no = hnos.join(", ");
      if (sbu) {
        payload.super_builtup_area_sqft = sbu;
        payload.carpet_area_sqft = carpet;
        payload.const_area_sqft = form.const_area_sqft || sbu;
      }
      if (!payload.total_area_plot_sqft && tot("plot_area_sqft")) payload.total_area_plot_sqft = tot("plot_area_sqft");
      if (aiResult) payload.ai_analysis = aiResult;
      if (approverId) payload.approver_id = approverId;

      // offline path: save the whole submission on the device and sync later
      const saveOffline = async () => {
        const local = (payload.photos || []).filter((p) => p.startsWith("data:"))
          .map((data, i) => ({ data, filename: `survey-offline-${i + 1}.jpg`, ...(photoGpsFor(data) || {}) }));   // Phase 132: GPS rides along
        payload.photos = (payload.photos || []).filter((p) => !p.startsWith("data:"));
        delete payload.survey_id; // the server assigns the real Survey ID during sync
        const fyList = fyIds.length ? fyIds : (masters.fyId ? [masters.fyId] : []);
        await queueAdd(code, "survey", payload, {
          photos: local,
          calcInputs: validFloors.length ? {
            fyIds: fyList, floors: validFloors,
            road_width_id: form.road_width_id || null, owner_name: form.owner_name,
          } : null,
          label: `New survey • ${form.owner_name}`,
        });
        toast.success("Survey saved on device — it will submit automatically when you're back online");
        navigate(`/${code}/collector/field`);
      };

      if (!online) { await saveOffline(); return; }
      // photos captured while briefly offline: upload them before submitting
      try {
        const localPhotos = (payload.photos || []).filter((p) => p.startsWith("data:"));
        if (localPhotos.length) {
          const urls = [];
          for (let i = 0; i < localPhotos.length; i++) {
            const gps = photoGpsFor(localPhotos[i]);   // Phase 132: captured with GPS on the device
            const r = await api.post(`/t/${code}/files/photo`, { data: localPhotos[i], filename: `survey-photo-${i + 1}.jpg`,
              ...(gps ? { ...gps, purpose: "field_photo" } : {}) });
            urls.push(r.data.url);
          }
          payload.photos = [...(payload.photos || []).filter((p) => !p.startsWith("data:")), ...urls];
        }
        const r = await api.post(`/t/${code}/surveys`, payload);
        toast.success(`Survey ${r.data.survey_id} submitted for approval${calc?.result ? ` • ${calc.result.demand_count} demand group(s) proposed` : ""}`);
        navigate(`/${code}/collector/field`);
      } catch (err) {
        if (isNetworkError(err)) { await saveOffline(); return; }
        throw err;
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold font-heading flex items-center gap-2"><FilePlus2 className="h-5 w-5" /> New property survey</h1>
      {!online && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-amber-900" data-testid="survey-offline-banner">
          <WifiOff className="h-4 w-4 shrink-0" />
          <p className="text-xs">You're offline — this survey is saved on your device and submits automatically when the network returns. The Survey ID and tax amount are assigned during sync.</p>
        </div>
      )}
      {svId && (
        <div className="rounded-lg border border-primary/30 bg-accent/50 px-3 py-2 flex items-center justify-between" data-testid="survey-id-banner">
          <span className="text-xs text-muted-foreground">New Survey ID</span>
          <span className="font-mono font-semibold text-sm text-primary" data-testid="survey-id-value">{svId}</span>
        </div>
      )}
      {online && (
        <AiScanCard code={code} photos={photos} setPhotos={setPhotos} onResult={setAiResult} onApply={applyScan}
          applyLabel="Apply estimates to floor editor" />
      )}
      <div className="space-y-3">
        <div className="space-y-1"><Label>Owner name *</Label><Input className="h-11" value={form.owner_name || ""} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="survey-owner-input" /></div>
        <div className="space-y-1"><Label>Father/Spouse name</Label><Input className="h-11" value={form.father_spouse_name || ""} onChange={(e) => setForm({ ...form, father_spouse_name: e.target.value })} /></div>
        <div className="space-y-1"><Label>Mobile</Label><Input className="h-11" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
        <div className="space-y-1"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="survey-address-input" /></div>
        <MasterSelect label="Zone *" options={zoneOpts} labelKey="zone_name" value={form.zone_id} onChange={(v) => setForm({ ...form, zone_id: v, ward_id: "", mohalla_id: "" })} testId="survey-zone-select" />
        <MasterSelect label="Ward *" options={wardOpts} labelKey="ward_name" value={form.ward_id} onChange={(v) => setForm({ ...form, ward_id: v, mohalla_id: "" })} testId="survey-ward-select" />
        <MasterSelect label="Mohalla" options={mohallaOpts} labelKey="mohalla_name" value={form.mohalla_id} onChange={(v) => setForm({ ...form, mohalla_id: v })} />
        <MasterSelect label="Road width" options={opts["road-widths"] || []} labelKey="width_label"
          value={form.road_width_id} onChange={(v) => setForm({ ...form, road_width_id: v })}
          testId="survey-roadwidth-select" />

        <FyMultiSelect fys={masters.fys} value={fyIds} onChange={setFyIds} testIdPrefix="survey-fy" />

        <Card className="shadow-sm">
          <CardContent className="p-3">
            <FloorEditorFull masters={masters} floors={floors} setFloors={setFloors} testIdPrefix="survey" />
          </CardContent>
        </Card>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Tax is assessed on constructed (super built-up) area — total {sbu.toLocaleString("en-IN")} sqft, carpet {carpet.toLocaleString("en-IN")} sqft (80%).
          Amounts are computed automatically from the municipality's rate configuration.
        </p>

        {online ? <NewDemandAmount calc={calc} /> : (
          <Card className="shadow-sm border-amber-300/60" data-testid="survey-offline-calc-note">
            <CardContent className="p-3">
              <p className="text-xs text-amber-900 flex items-center gap-2">
                <CloudUpload className="h-4 w-4 shrink-0" />
                The demand amount is calculated automatically from the municipality's rate configuration when this survey syncs.
              </p>
            </CardContent>
          </Card>
        )}

        <PhotoCapture code={code} photos={photos} setPhotos={setPhotos} />
        {online ? (
          <DocumentUploads code={code} docTypes={masters.docTypes} docs={docs} setDocs={setDocs}
            dueDate={docDueDate} setDueDate={setDocDueDate} testIdPrefix="survey-docs" />
        ) : (
          <p className="text-[11px] text-muted-foreground border border-dashed rounded-lg px-3 py-2" data-testid="survey-offline-docs-note">
            Document upload needs internet — after this survey syncs, attach documents from Field operations → My submissions.
          </p>
        )}
        <GpsButton gps={gps} onCapture={captureGps} testId="survey-gps-button" />
        <ApproverSelect code={code} value={approverId} onChange={setApproverId} testId="survey-approver-select" />
        <Button size="lg" className="w-full h-12" onClick={submit} disabled={busy} data-testid="survey-submit-button">
          {busy ? "Submitting..." : !online ? "Save survey on device (offline)"
            : calc?.result ? `Submit survey (${fmtINR(calc.result.grand_total?.total_yearly || 0)}/yr proposed)` : "Submit survey"}
        </Button>
      </div>
    </div>
  );
}

export default function FieldHub() {
  const { code } = useParams();
  // Phase 152: "field.reassessments" / "field.surveys" sub-features (hidden cards when No access)
  const access = useRoleModules(code);
  const showReassess = access.featureAllowed("field", "reassessments");
  const showSurvey = access.featureAllowed("field", "surveys");
  const showSaf = access.featureAllowed("field", "saf_verification");   // Phase 207
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const masters = useAssessmentMasters(code);
  const online = useOnline();
  const queue = useQueue(code);
  const [syncBusy, setSyncBusy] = useState(false);
  const [reassessments, setReassessments] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [docTarget, setDocTarget] = useState(null); // {kind, item}
  const [laterDocs, setLaterDocs] = useState([]);
  const [laterDue, setLaterDue] = useState("");
  const [savingDocs, setSavingDocs] = useState(false);

  const load = () => {
    if (showReassess) api.get(`/t/${code}/reassessments`).then((r) => setReassessments(r.data)).catch(() => {});
    if (showSurvey) api.get(`/t/${code}/surveys`).then((r) => setSurveys(r.data)).catch(() => {});
  };
  // reload submissions whenever the sync queue drains (offline items just synced)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [code, queue.length, showReassess, showSurvey]);

  const openDocs = (kind, item) => {
    setDocTarget({ kind, item });
    setLaterDocs([]);
    setLaterDue(item.doc_collect_date || "");
  };

  // deep-link from the home reminder card (sessionStorage handoff survives guard redirects)
  useEffect(() => {
    const target = searchParams.get("docs") || sessionStorage.getItem("open_docs_target");
    if (!target || docTarget) return;
    const [kind, id] = target.split(":");
    const pool = kind === "reassessments" ? reassessments : surveys;
    const item = pool.find((x) => x.id === id);
    if (item) {
      sessionStorage.removeItem("open_docs_target");
      openDocs(kind, item);
      if (searchParams.get("docs")) setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, reassessments, surveys]);

  const saveLaterDocs = async () => {
    if (!docTarget) return;
    setSavingDocs(true);
    try {
      const r = await api.put(`/t/${code}/field/${docTarget.kind}/${docTarget.item.id}/docs`, {
        documents: laterDocs, doc_collect_date: laterDue,
      });
      const msg = r.data.files_added > 0
        ? `${r.data.files_added} document file(s) attached to ${docTarget.item.reassessment_id || docTarget.item.survey_id}`
        : "Saved";
      toast.success(laterDue ? msg : `${msg} — pending-document reminder cleared`);
      setDocTarget(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingDocs(false); }
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    try {
      const r = await api.get(`/t/${code}/properties`, { params: { search: query.trim(), limit: 8 } });
      setResults(r.data.items || []);
      if ((r.data.items || []).length === 0) toast.info("No property found for this search");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold font-heading">Field operations</h1>

      {queue.length > 0 && (
        <Card className="shadow-sm border-amber-300/70" data-testid="pending-sync-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <CloudUpload className="h-4 w-4" /> Pending sync ({queue.length})
              </span>
              {online && (
                <Button size="sm" className="h-8" onClick={async () => {
                  setSyncBusy(true);
                  const res = await syncAll(code);
                  setSyncBusy(false);
                  if (res) {
                    if (res.synced > 0) toast.success(`${res.synced} item(s) synced successfully`);
                    if (res.failed > 0) toast.error(`${res.failed} item(s) could not sync — check the list below`);
                    if (res.remaining > 0) toast.info("Connection dropped — remaining items will retry automatically");
                  }
                  load();
                }} disabled={syncBusy} data-testid="pending-sync-now-button">
                  {syncBusy ? "Syncing..." : "Sync now"}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {!online && (
              <p className="text-xs text-amber-900 flex items-center gap-1.5">
                <WifiOff className="h-3.5 w-3.5" /> Offline — these items sync automatically when the network returns.
              </p>
            )}
            {queue.map((it) => (
              <div key={it.id} className="flex items-center justify-between border rounded-lg px-3 py-2 gap-2" data-testid={`pending-sync-item-${it.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {it.label || ({ survey: "New survey", reassessment: "Reassessment", visit: "Visit log", mark_visited: "Marked visited", mobile_verify: "Mobile number verified" }[it.kind] || it.kind)}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    Saved {fmtDateTimeLocal(it.created_at)}{(it.photos || []).length ? ` • ${it.photos.length} photo(s)` : ""}
                  </p>
                  {it.status === "failed" && (
                    <p className="text-[11px] text-red-600 truncate" title={it.error} data-testid={`pending-sync-error-${it.id}`}>
                      Failed: {it.error}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-xs font-medium capitalize px-2 py-1 rounded-full ${
                    it.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                    {it.status}
                  </span>
                  {it.status === "failed" && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground"
                      onClick={async () => { await removeQueueItem(it.id); toast.info("Removed from the sync queue"); }}
                      title="Discard this item" data-testid={`pending-sync-discard-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {showReassess && <Card className="shadow-sm border-primary/30" data-testid="field-reassess-card">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><RefreshCcw className="h-4 w-4" /> Reassess a property</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Search the municipal records (uploaded by the admin) by PIN, owner or address, review every
            field on record including floor-wise details, then retain them as-is or submit revised values.
          </p>
          <form onSubmit={search} className="flex gap-2">
            <Input className="h-11" placeholder="PIN / owner / house no / address..." value={query}
              onChange={(e) => setQuery(e.target.value)} data-testid="reassess-search-input" />
            <Button type="submit" className="h-11" data-testid="reassess-search-button"><SearchIcon className="h-4 w-4" /></Button>
          </form>
          {results !== null && results.map((p) => (
            <div key={p.id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`reassess-result-${p.pin}`}>
              <div className="min-w-0">
                <p className="text-sm font-mono font-medium">{p.pin} <span className="font-sans font-normal">• {p.owner_name}</span></p>
                <p className="text-xs text-muted-foreground truncate">
                  {p.ward_label || ""} {p.total_balance > 0 ? `• ${fmtINR(p.total_balance)} due` : "• no dues"}
                </p>
              </div>
              <Button asChild size="sm" variant="outline" data-testid={`reassess-open-${p.pin}`}>
                <Link to={`/${code}/collector/reassessment/${p.id}`}>View & reassess</Link>
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>}

      {showSurvey && <Card className="shadow-sm" data-testid="field-survey-card">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">New property survey</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">Found an unregistered property? Survey it with photos and GPS. Admin approval creates the property + PIN.</p>
          <Button asChild className="w-full h-11" data-testid="start-survey-button">
            <Link to={`/${code}/collector/survey`}><FilePlus2 className="h-4 w-4 mr-2" /> Start new survey</Link>
          </Button>
        </CardContent>
      </Card>}

      {/* Phase 207: citizen self-assessments assigned to this collector for physical verification */}
      {showSaf && <Card className="shadow-sm" data-testid="field-saf-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> SAF Verification</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            Citizen self-assessments assigned to you. Visit the property, verify every detail and floor, enter the final demand
            and submit for Supervisor review. Cases sent back for correction return here.
          </p>
          <SafQueue code={code} onOpen={(s) => navigate(`/${code}/collector/saf/${s.id}`)} testIdPrefix="field-saf" pendingLabel="To verify" limit={5} compact />
        </CardContent>
      </Card>}
      {access.loaded && !showReassess && !showSurvey && !showSaf && <NoAccessPanel what="Field forms" testId="field-forms-no-access" />}

      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">My submissions</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {[...reassessments.map((r) => ({ ...r, _label: r.reassessment_id, _type: "Reassessment", _kind: "reassessments" })),
            ...surveys.map((s) => ({ ...s, _label: s.survey_id, _type: s.source === "self_assessment" ? "Self-assessment" : "Survey", _kind: "surveys" }))]
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
            .map((it) => (
              <div key={it.id} className={`flex items-center justify-between border rounded-lg px-3 py-2 gap-2 ${it.status === "rejected" ? "border-red-200 bg-red-50/40" : ""}`}>
                <div className="min-w-0">
                  <p className="text-sm font-mono font-medium">{it._label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {it._type} • {it.owner_name || it.pin || ""}
                    {(it.documents || []).length > 0 ? ` • ${(it.documents || []).reduce((s, d) => s + (d.files || []).length, 0)} doc(s)` : ""}
                    {it.doc_collect_date ? ` • docs due ${it.doc_collect_date}` : ""}
                  </p>
                  {it.status === "rejected" && (
                    <p className="text-[11px] text-red-700 mt-0.5" title={it.review_remarks || ""}
                      data-testid={`rejection-reason-${it._label}`}>
                      Rejected{it.reviewed_by_name ? <> by <StaffLabel code={code} userId={it.reviewed_by} name={it.reviewed_by_name} empCode={it.reviewed_by_employee_id} /></> : ""}: {it.review_remarks || "No reason provided"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {it.status === "rejected" && (
                    <Button variant="outline" size="sm" className="h-8 px-2 border-red-200 text-red-700 hover:bg-red-50" asChild
                      title={it._kind === "reassessments" ? "Fix & redo this reassessment" : "Fix & submit a corrected survey"}>
                      <Link to={it._kind === "reassessments"
                        ? `/${code}/collector/reassessment/${it.property_id}`
                        : `/${code}/collector/survey`}
                        data-testid={`resubmit-${it._label}`}>
                        <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Redo
                      </Link>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => openDocs(it._kind, it)}
                    data-testid={`add-docs-${it._label}`} title="Add documents">
                    <Paperclip className="h-3.5 w-3.5" />
                  </Button>
                  <span className={`text-xs font-medium capitalize px-2 py-1 rounded-full ${
                    it.status === "approved" ? "bg-emerald-50 text-emerald-700" : it.status === "rejected" ? "bg-red-50 text-red-700" : "bg-sky-50 text-sky-700"
                  }`}>{it.status}</span>
                </div>
              </div>
            ))}
          {reassessments.length === 0 && surveys.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No submissions yet</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!docTarget} onOpenChange={(v) => !v && setDocTarget(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="later-docs-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">
              Add documents — {docTarget?.item?.reassessment_id || docTarget?.item?.survey_id}
            </DialogTitle>
          </DialogHeader>
          {docTarget && (
            <div className="space-y-3">
              {(docTarget.item.documents || []).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Already attached: {(docTarget.item.documents || []).map((d) => `${d.doc_label} (${(d.files || []).length})`).join(", ")}
                </p>
              )}
              <DocumentUploads code={code} docTypes={masters.docTypes} docs={laterDocs} setDocs={setLaterDocs}
                dueDate={laterDue} setDueDate={setLaterDue} testIdPrefix="later-docs" />
              <Button className="w-full" onClick={saveLaterDocs}
                disabled={savingDocs || (laterDocs.length === 0 && !laterDue && !docTarget.item.doc_collect_date)}
                data-testid="later-docs-save-button">
                {savingDocs ? "Saving..." : "Attach to submission"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
